import { Autorenew, ContentCopy, NoAccounts } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Chip,
  Stack
} from "@mui/material";
import React from "react";
import { NavigateFunction } from "react-router-dom";
import { logout } from "../../../SharedComponents/LoginGuard/loginGuard";
import { Pagination } from "../../../SharedComponents/Pagination";
import { SearchInput } from "../../../SharedComponents/SearchInput";
import { environment } from "../../../utils/environment";
import { calculateTotalPages } from "../../../utils/pagination";

import {
  ManagedUsersController,
  ManagedUsersControllerDelegate,
} from "../../controllers/ManagedUsersController";
import { IManagedUser } from "../../types";

import AddInstrumentModal, {
  AddInstrumentModalDelegate,
} from "../modals/AddInstrumentModal/AddInstrumentModal";
import DeleteAssignmentModal, {
  DeleteAssignmentModalDelegate,
} from "../modals/DeleteAssignmentModal/DeleteAssignmentModal";
import InviteUserModal, {
  InviteUserModalDelegate,
} from "../modals/InviteUserModal/InviteUserModal";

import EmptyState from "../../../SharedComponents/EmptyState/EmptyState";
import { withNavigationHooks } from "../../../SharedComponents/withNavigationHooks";
import InvitedUsersRow, { ManagedUserRowDelegate } from "./InvitedUsersRow";

interface State {
  data: {
    users: IManagedUser[];
    selectedUser?: string;
    deleteAssignmentData?: {
      userUid: string;
      assignmentId: number;
    };
  };
  isLoading: boolean;
  openedInviteUserModal: boolean;
  openedDeleteApplicationModal: boolean;
  snackbarOpen: boolean;
  snackbarMessage: string;
  snackbarSeverity: "success" | "error" | "info" | "warning";
  /**
   * Codes revealed in this session, keyed by assignmentId. Kept here rather than
   * in the listing because the listing deliberately never carries code values —
   * each entry is one deliberate click on "ver".
   */
  revealedCodes: Record<number, string | null>;
  /**
   * The code a regenerate just produced, held to offer delivery. Regenerating
   * always invalidates the previous value, so the respondent is left holding a
   * dead code until someone sends the new one — this is what turns that repair
   * into an offer instead of something to remember.
   */
  regenerated?: { assignmentId: number; code: string };
}

interface Props {
  navigationHooks: NavigateFunction;
}

class InvitedUsersTab
  extends React.Component<Props, State>
  implements
  ManagedUsersControllerDelegate,
  ManagedUserRowDelegate,
  AddInstrumentModalDelegate,
  InviteUserModalDelegate,
  DeleteAssignmentModalDelegate {
  private controller: ManagedUsersController;
  private filters = { filter: "", page: 1, total: 1, limit: 10 };

  constructor(props: Props) {
    super(props);
    this.controller = new ManagedUsersController(this);
    this.state = {
      data: { users: [] },
      isLoading: true,
      openedInviteUserModal: false,
      openedDeleteApplicationModal: false,
      snackbarOpen: false,
      snackbarMessage: "",
      snackbarSeverity: "success",
      revealedCodes: {},
    };
  }

  componentDidMount() {
    this.loadData();
  }

  didGetUsers(users: IManagedUser[]) {
    this.setState({
      isLoading: false,
      data: { ...this.state.data, users },
    });
  }

  filter({
    page,
    total,
  }: {
    page: number;
    total: number;
    filter: string;
  }): void {
    this.filters.page = page;
    this.filters.total = total;
  }

  applicationShouldLogout() {
    logout();
  }

  genericError(message?: string) {
    this.setState({
      isLoading: false,
      openedInviteUserModal: false,
      openedDeleteApplicationModal: false,
      snackbarOpen: true,
      snackbarMessage: message ?? "Ocorreu um erro inesperado.",
      snackbarSeverity: "error",
    });
  }

  didGetAssignmentToken(token: string) {
    this.setState({ isLoading: false });
    window.open(`${environment.feedbackBaseUrl}?jwt=${token}`, "_newtab");
  }

  didClickAddInstrument(userUid: string) {
    this.setState({
      data: { ...this.state.data, selectedUser: userUid },
    });
  }

  didClickSeeResults(assignmentId: number) {
    this.setState({ isLoading: true }, () => {
      const token = localStorage.getItem("AccessToken")!;
      this.controller.getAssignmentFeedbackToken(token, assignmentId);
    });
  }

  didClickDeleteAssignment(assignmentId: number, userUid: string) {
    this.setState({
      openedDeleteApplicationModal: true,
      data: {
        ...this.state.data,
        deleteAssignmentData: { assignmentId, userUid },
      },
    });
  }

  // ACCESS CODE — the three credential actions (reveal/share, resend, regenerate)

  /**
   * Whether the listed user finished registration — the same discriminator the
   * row uses. `undefined` when the identity resolved through neither table, so
   * the modal says "if" instead of asserting an outcome it cannot know.
   */
  private targetHasAccount(userUid: string): boolean | undefined {
    const user = this.state.data.users.find(
      (candidate) => candidate.uid === userUid
    );
    if (!user?.identity) return undefined;
    return user.identity.isInvitation === false;
  }

  didClickCopyAccessShare(assignmentId: number) {
    this.setState({ isLoading: true }, () => {
      const token = localStorage.getItem("AccessToken")!;
      this.controller.copyAccessCodeShare(token, assignmentId);
    });
  }

  didClickSendAccessEmail(assignmentId: number) {
    this.setState({ isLoading: true }, () => {
      const token = localStorage.getItem("AccessToken")!;
      this.controller.sendAccessCodeEmail(token, assignmentId);
    });
  }

  didClickRegenerateAccessCode(assignmentId: number) {
    this.setState({ isLoading: true }, () => {
      const token = localStorage.getItem("AccessToken")!;
      this.controller.regenerateAccessCode(token, assignmentId);
    });
  }

  didClickRevealAccessCode(assignmentId: number) {
    const token = localStorage.getItem("AccessToken")!;
    this.controller.revealAccessCode(token, assignmentId);
  }

  didRevealAccessCode(assignmentId: number, code: string | null) {
    this.setState({
      revealedCodes: { ...this.state.revealedCodes, [assignmentId]: code },
    });
  }

  didCopyShareText() {
    this.setState({
      isLoading: false,
      snackbarOpen: true,
      snackbarMessage: "Texto de acesso copiado!",
      snackbarSeverity: "success",
    });
  }

  /**
   * Deliberately neutral wording: the backend picks the template from the
   * respondent's state and answers 204 without saying which, so this cannot
   * honestly claim "acesso" (code share) over "notificação" (login share).
   */
  didSendShareEmail() {
    this.setState({
      isLoading: false,
      snackbarOpen: true,
      snackbarMessage: "E-mail enviado.",
      snackbarSeverity: "success",
    });
  }

  /**
   * Reloads the listing so the `Acesso` chip reflects the new state — a
   * regenerate clears a `locked` status, and leaving the stale chip on screen
   * would suggest the respondent is still blocked. The wording deliberately
   * stops short of "acesso revogado": nothing here ends a session already
   * minted with the old code.
   */
  /**
   * Opens the delivery offer instead of a snackbar: a 6-second toast is the
   * wrong carrier for something that needs an action, and the action here is
   * what keeps the respondent from being stranded with the old code.
   */
  didRegenerateCode(assignmentId: number, code: string) {
    this.setState(
      {
        isLoading: true,
        regenerated: { assignmentId, code },
        // Overwrite rather than clear: leaving the old value on screen would
        // show a code that no longer authenticates.
        revealedCodes: { ...this.state.revealedCodes, [assignmentId]: code },
      },
      () => this.loadData()
    );
  }

  private dismissRegenerated = () => {
    this.setState({ regenerated: undefined });
  };

  private sendRegeneratedByEmail = () => {
    const target = this.state.regenerated;
    if (!target) return;
    this.setState({ regenerated: undefined }, () =>
      this.didClickSendAccessEmail(target.assignmentId)
    );
  };

  /** Reuses the resend action, so delivery reports through the same snackbars. */
  private renderRegeneratedDialog() {
    const regenerated = this.state.regenerated;
    if (!regenerated) return null;

    return (
      <Dialog
        open
        onClose={this.dismissRegenerated}
        variant="modal"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle variant="modal">
          <Autorenew />
          Novo código gerado
        </DialogTitle>
        <DialogContent variant="modal">
          <Box display="flex" flexDirection="column" sx={{ gap: 2 }}>
            <Typography
              variant="h4"
              fontWeight={600}
              textAlign="center"
              sx={{ fontFamily: "monospace", letterSpacing: "0.15em" }}
            >
              {regenerated.code}
            </Typography>
            <Typography color="text.secondary">
              O código anterior parou de funcionar. Envie o novo para o
              respondente conseguir entrar.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Quem já entrou continua até a sessão expirar.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          variant="modal"
          sx={{ flexWrap: "wrap", gap: 1, justifyContent: "end" }}
        >
          <Button
            variant="outlined"
            color="error"
            onClick={this.dismissRegenerated}
          >
            Agora não
          </Button>
          <Button variant="contained" onClick={this.sendRegeneratedByEmail}>
            Enviar por e-mail
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  /** 409 — retrying cannot help, so it reads as a warning, not an error. */
  shareStateChanged(message: string) {
    this.setState({
      isLoading: false,
      snackbarOpen: true,
      snackbarMessage: message,
      snackbarSeverity: "warning",
    });
  }

  shouldCloseModal() {
    this.setState({
      data: { ...this.state.data, selectedUser: undefined },
      openedInviteUserModal: false,
      openedDeleteApplicationModal: false,
    });
  }

  didAddAssignments() {
    this.setState(
      {
        isLoading: true,
        data: { ...this.state.data, selectedUser: undefined },
      },
      () => this.loadData()
    );
  }

  didInviteUser() {
    this.setState({ isLoading: true, openedInviteUserModal: false }, () =>
      this.loadData()
    );
  }

  didDeleteAssignment() {
    this.setState(
      { isLoading: true, openedDeleteApplicationModal: false },
      () => this.loadData()
    );
  }

  handleOpenInvitationModal = () =>
    this.setState({ openedInviteUserModal: true });

  handleCopyInstrumentsUrl = () => {
    navigator.clipboard.writeText(environment.instrumentsFrontendBaseUrl).then(() => {
      this.setState({
        snackbarOpen: true,
        snackbarMessage: "Link copiado com sucesso!",
        snackbarSeverity: "success",
      });
    });
  };

  handleSnackbarClose = () => this.setState({ snackbarOpen: false });

  private loadData() {
    const token = localStorage.getItem("AccessToken")!;
    const params: any = { ...this.filters };
    delete params.total;
    Object.keys(params).forEach((k) => params[k] === "" && delete params[k]);
    this.controller.getManagedUsers(token, params);
  }

  handlePageChange = (page: number) => {
    const totalPages =
      calculateTotalPages(this.filters.total, this.filters.limit) || 1;
    const nextPage = Math.min(Math.max(page, 1), totalPages);
    if (nextPage === this.filters.page) {
      return;
    }
    this.filters.page = nextPage;
    this.loadData();
  };

  render() {
    const {
      isLoading,
      data: { users, selectedUser, deleteAssignmentData },
      openedInviteUserModal,
      openedDeleteApplicationModal,
      snackbarOpen,
      snackbarMessage,
      snackbarSeverity,
    } = this.state;

    if (isLoading && users.length === 0) {
      return (
        <Box
          flex={1}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box display="flex">
        <Box flex={1} overflow="auto">
          <Container maxWidth="lg"
            style={{
              padding: "40px 20px",
            }}
          >
            <Box
              marginBottom={2}
              sx={{
                display: 'flex',
                flexDirection: {
                  xs: 'column',
                  sm: 'row'
                },
                gap: '0.75rem',
                alignItems: {
                  sm: 'center'
                },
                justifyContent: {
                  xs: 'space-between',
                },
                flexWrap: 'wrap'
              }}
            >
              <Stack direction={"column"} spacing={1.25}>
                <Box display="flex" alignItems="center" gap={1} flexWrap={'wrap'}>
                  <Typography variant="h2">
                    Respondentes
                  </Typography>
                  
                  <Chip
                    label={`${this.filters?.total} ${this.filters?.total !== 1 ? "encontrados" : "encontrado"}`}
                    size="small" 
                  />
                </Box>

                <Typography variant="subtitle1" color={'text.secondary'}>
                  Controle e visualize a participação de respondentes convidados
                </Typography>
              </Stack>

              <Box display="flex" gap={1} flexWrap="wrap">
                <Tooltip title={environment.instrumentsFrontendBaseUrl} arrow>
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<ContentCopy />}
                    onClick={this.handleCopyInstrumentsUrl}
                  >
                    Copiar link de acesso como respondente
                  </Button>
                </Tooltip>

                <Button
                  variant="contained"
                  size="medium"
                  onClick={this.handleOpenInvitationModal}
                >
                  Convidar usuário
                </Button>
              </Box>
            </Box>
              
            <Box
              marginBottom={2}
              sx={{
                display: 'flex',
                flexDirection: {
                  xs: 'column',
                  sm: 'row'
                },
                gap: '0.75rem',
                alignItems: {
                  sm: 'center'
                },
                justifyContent: {
                  xs: 'start',
                  md: 'end'
                },
                flexWrap: 'wrap'
              }}
            >
              <SearchInput
                label="Pesquisar Usuário"
                onChangeFilter={(f) => (this.filters.filter = f)}
                onSearch={(filter) => {
                  this.filters.filter = filter;
                  this.filters.page = 1;
                  this.loadData();
                }}
              />
            </Box>

            <TableContainer component={Paper}>
              <Table aria-label="users-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="center">N.º de Instrumentos</TableCell>
                    <TableCell align="center">Status do Convite</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <EmptyState
                          icon={NoAccounts}
                          title="Nenhum usuário encontrado"
                          description="Tente ajustar os filtros ou adicionar novos usuários."
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((u) => (
                      <InvitedUsersRow
                        key={u.uid}
                        user={u}
                        delegate={this}
                        revealedCodes={this.state.revealedCodes}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Pagination
              currentPage={this.filters.page}
              totalPages={calculateTotalPages(
                this.filters.total,
                this.filters.limit
              )}
              onChangePage={this.handlePageChange}
            />
          </Container>
        </Box>

        {selectedUser && (
          <AddInstrumentModal
            userUid={selectedUser}
            delegate={this}
            targetHasAccount={this.targetHasAccount(selectedUser)}
          />
        )}
        {openedInviteUserModal && <InviteUserModal delegate={this} />}
        {openedDeleteApplicationModal && deleteAssignmentData && (
          <DeleteAssignmentModal
            userUid={deleteAssignmentData.userUid}
            assignmentId={deleteAssignmentData.assignmentId}
            delegate={this}
          />
        )}

        {this.renderRegeneratedDialog()}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={this.handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert
            onClose={this.handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
}

export default withNavigationHooks(InvitedUsersTab);
